// src/components/editable/FullFieldModal.tsx
// Modal listing all schema fields for a clinical row, with search and inline editing.

import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import TableChartIcon from "@mui/icons-material/TableChart";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  alpha,
  Box,
  Button,
  Collapse,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { FieldInfo } from "hooks/useFieldSchema";
import { useTranslation } from "../../i18n";
import {
  athenaBackdropSx,
  athenaColors,
  athenaDarkSecondaryButtonSx,
  athenaHeaderIconBoxSx,
} from "../layout/AthenaDialogStyles";
import EditableCell from "./EditableCell";
import { ImageThumbnail } from "./ImageThumbnail";
import ImageLightbox from "./ImageLightbox";
import AppBackdrop from "../dashboards/appBackdrop";

const EXCLUDED_FIELDS = [
  "id",
  "event_id",
  "created_at",
  "modified_at",
  "created_by",
  "batch_id",
  "event_no",
  "text",
  "table",
  "processing_task_id",
  "processing_status",
  "patient",
  "environments",
  "platform_user",
  "is_validated",
  "progress",
  "eventtable_ptr",
  "found_with_parent",
  "institution_id",
  "dynamic_entities",
  "starter",
  "source",
  "is_locked",
  "last_ai_extraction",
  "source_documents",
  "locked_fields",
  "human_edited_fields",
  // Internal backfill/sync audit blob — never user-visible (CLAUDE.md rule).
  "migration_notes",
  // oncology-course anchor FK on the residue tables — relational, not clinical.
  "cancer", "cancer_id",
  // Imaging image-display wiring (P1): source-image link + resolved URL are shown
  // as a dedicated image preview, never as editable fields.
  "image_document", "image_url",
];

const normalizeKeyName = (key: string) =>
  key
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

function getDisplayName(fieldName: string, info: FieldInfo | undefined): string {
  const v = info?.verbose_name?.trim();
  return v || normalizeKeyName(fieldName);
}

function isEmptyValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (v === "") return true;
  if (Array.isArray(v) && v.length === 0) return true;
  if (typeof v === "object" && !Array.isArray(v) && Object.keys(v as object).length === 0) {
    return true;
  }
  return false;
}

export interface FullFieldModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** All fields from schema */
  fields: Record<string, FieldInfo>;
  /** Field categories for static tables: {"Category Name": ["field1", "field2"]} */
  fieldCategories?: Record<string, string[]>;
  /** Field event type relevance for event tables */
  fieldEventCategories?: Record<string, string[]>;
  /** Current row data (field values) */
  data: Record<string, any>;
  /** Row ID for save operations */
  rowId: number | string;
  /** Imaging rows whose `cancer` FK points at THIS row (Diagnosis detail only).
   *  When non-empty, a small thumbnail gallery of the linked lesion images is
   *  surfaced at the top of the detail body. Each item exposes a resolved
   *  `image_url`; the source image keeps its FHIR home in the Imaging panel. */
  linkedImages?: Array<Record<string, any>>;
  /** Whether the record is locked */
  isLocked?: boolean;
  /** Per-field locks (cursor_logs/039): field names protected from AI overwrite. */
  lockedFields?: string[];
  /** Toggle a single field's lock (cursor_logs/039). `nextLocked` is the
   *  desired state after the click. */
  onToggleFieldLock?: (fieldName: string, nextLocked: boolean) => void;
  /** When true, per-field "edited/created by user" badges are derived from the
   *  record's `human_edited_fields` provenance list (cursor_logs/040) instead of
   *  the record-level `source`. Set for tables in PER_FIELD_PROVENANCE_KINDS.
   *  Deliberately decoupled from `onToggleFieldLock` so a table can show
   *  per-field badges without also exposing a lock toggle. */
  perFieldProvenance?: boolean;
  /** Callback to save a field value */
  onSave: (fieldName: string, value: any) => Promise<void>;
  /** When set, only show fields from this specific category */
  filterCategory?: string;
  /** When set, the modal opens scrolled to (and highlighting in the TOC) this
   *  section title — but unlike `filterCategory` ALL sections remain visible
   *  with their sidebar. Drives the per-section pencils in EditableTable. */
  initialSection?: string;
}

const categoryHeaderSx = {
  textTransform: "uppercase" as const,
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: 1,
  color: athenaColors.periwinkle,
  borderBottom: `1px solid ${alpha(athenaColors.periwinkle, 0.35)}`,
  pb: 0.75,
  pt: 1.5,
  mb: 1,
};

const fieldNameSx = {
  fontSize: "0.82rem",
  fontWeight: 500,
  color: alpha(athenaColors.white, 0.9),
};

const fieldRowGridSx = {
  display: "grid",
  gridTemplateColumns: "40% 60%",
  columnGap: 2,
  alignItems: "start",
  py: 0.75,
  borderBottom: `1px solid ${alpha(athenaColors.periwinkle, 0.12)}`,
};

const glassVideoPaperSx = {
  borderRadius: 3,
  overflow: "hidden",
  background: "transparent",
  boxShadow: `0 24px 80px ${alpha(athenaColors.deepNavy, 0.5)}`,
};

const FullFieldModal: React.FC<FullFieldModalProps> = ({
  open,
  onClose,
  title,
  fields,
  fieldCategories,
  fieldEventCategories,
  data,
  rowId,
  linkedImages = [],
  isLocked = false,
  lockedFields = [],
  onToggleFieldLock,
  perFieldProvenance = false,
  onSave,
  filterCategory,
  initialSection,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Desktop-only: the table-of-contents nav appears on wide screens (>= lg).
  // Below that the modal renders exactly as before (single scrolling column).
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [search, setSearch] = useState("");
  const [showOtherCategories, setShowOtherCategories] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");
  // In-app image lightbox. Drives both the Diagnosis lesion-image gallery
  // (a carousel over all linked images) and the single Imaging-row detail image.
  // `images` holds the active set so the same overlay serves both.
  const [lightbox, setLightbox] = useState<{ open: boolean; index: number; images: string[] }>({
    open: false,
    index: 0,
    images: [],
  });

  // Right-panel scroll container + per-section elements, for the TOC nav.
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const registerSectionRef = useCallback(
    (title: string) => (el: HTMLElement | null) => {
      if (el) sectionRefs.current.set(title, el);
      else sectionRefs.current.delete(title);
    },
    []
  );

  const searchTrim = search.trim();
  const searchLower = searchTrim.toLowerCase();

  const baseFieldKeys = useMemo(
    () => Object.keys(fields).filter((k) => !EXCLUDED_FIELDS.includes(k)),
    [fields]
  );

  const filteredFieldKeys = useMemo(() => {
    return baseFieldKeys.filter((key) => {
      if (!searchLower) return true;
      const info = fields[key];
      const label = getDisplayName(key, info).toLowerCase();
      return label.includes(searchLower) || key.toLowerCase().includes(searchLower);
    });
  }, [baseFieldKeys, fields, searchLower]);

  type Section = { title: string; keys: string[] };

  const { primarySections, otherSections } = useMemo(() => {
    const primary: Section[] = [];
    const other: Section[] = [];

    if (filteredFieldKeys.length === 0) return { primarySections: primary, otherSections: other };

    if (fieldCategories && Object.keys(fieldCategories).length > 0) {
      const allRendered = new Set<string>();

      for (const [catName, catKeys] of Object.entries(fieldCategories)) {
        const keys = catKeys.filter((k) => filteredFieldKeys.includes(k) && !allRendered.has(k));
        keys.forEach((k) => allRendered.add(k));
        if (keys.length === 0) continue;
        const section: Section = { title: catName, keys };
        if (filterCategory && catName !== filterCategory) {
          other.push(section);
        } else {
          primary.push(section);
        }
      }

      const uncategorized = filteredFieldKeys.filter((k) => !allRendered.has(k));
      if (uncategorized.length > 0) {
        const section: Section = {
          title: t("fullFieldModal.other_category", "Other"),
          keys: uncategorized.sort((a, b) =>
            getDisplayName(a, fields[a]).localeCompare(getDisplayName(b, fields[b]))
          ),
        };
        if (filterCategory) {
          other.push(section);
        } else {
          primary.push(section);
        }
      }

      return { primarySections: primary, otherSections: other };
    }

    const eventTypeRaw = data.event_type;
    const eventTypeKey =
      eventTypeRaw !== null && eventTypeRaw !== undefined && String(eventTypeRaw).trim() !== ""
        ? String(eventTypeRaw)
        : null;

    if (fieldEventCategories && eventTypeKey) {
      const universalFields = fieldEventCategories["_universal"] ?? [];
      const relevantList = [...universalFields, ...(fieldEventCategories[eventTypeKey] ?? [])];
      const relevantKeys = relevantList.filter(
        (k, i, arr) => filteredFieldKeys.includes(k) && arr.indexOf(k) === i
      );
      if (relevantKeys.length > 0) {
        primary.push({ title: "", keys: relevantKeys });
      }
      return { primarySections: primary, otherSections: other };
    }

    primary.push({
      title: "",
      keys: [...filteredFieldKeys].sort((a, b) =>
        getDisplayName(a, fields[a]).localeCompare(getDisplayName(b, fields[b]))
      ),
    });
    return { primarySections: primary, otherSections: other };
  }, [data.event_type, fieldCategories, fieldEventCategories, fields, filteredFieldKeys, filterCategory, t]);

  const handleToggleOther = useCallback(() => {
    setShowOtherCategories((prev) => !prev);
  }, []);

  // Titled primary sections become the table-of-contents items. Untitled
  // single-section tables (event tables grouped by event type, or flat
  // schemas) get no nav — there is nothing to jump between.
  const navSections = useMemo(
    () => primarySections.filter((s) => s.title),
    [primarySections]
  );
  const showNav = isDesktop && navSections.length >= 2;

  // Smooth-scroll the right panel to a section (offset so the header isn't
  // flush against the top). Uses getBoundingClientRect so it works regardless
  // of how deeply the section is nested in the scroll container.
  const scrollToSection = useCallback((title: string) => {
    const container = scrollRef.current;
    const el = sectionRefs.current.get(title);
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    container.scrollTo({
      top: container.scrollTop + (eRect.top - cRect.top) - 8,
      behavior: "smooth",
    });
    setActiveSection(title);
  }, []);

  // Deep-link to a specific section on open (driven by EditableTable's per-section
  // pencils). Defer one tick so the section refs are registered before we try to
  // scroll. `instant` would feel jarring, so we still use the smooth animation
  // inside scrollToSection — at modal-open time the user perceives this as
  // "the modal opened on that section".
  useEffect(() => {
    if (!open || !initialSection) return;
    const id = window.setTimeout(() => scrollToSection(initialSection), 80);
    return () => window.clearTimeout(id);
  }, [open, initialSection, scrollToSection]);

  // Highlight the section currently at the top of the scroll viewport.
  useEffect(() => {
    if (!showNav || !open) return;
    const container = scrollRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const title = (visible[0]?.target as HTMLElement | undefined)?.dataset
          .sectionTitle;
        if (title) setActiveSection(title);
      },
      // Bias toward the top: a section is "active" once it reaches the upper
      // third of the viewport.
      { root: container, rootMargin: "0px 0px -66% 0px", threshold: 0 }
    );
    sectionRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [showNav, open, navSections, searchLower]);


  // Per-field provenance for the indicators (mirrors EditableTable). Tables in
  // PER_FIELD_PROVENANCE_KINDS track ownership per field via `human_edited_fields`
  // — the dedicated provenance list (cursor_logs/040). Unlike `locked_fields`, it
  // is NOT cleared on unlock, so a field a human edited keeps its "edited by user"
  // badge after unlocking, until AI overwrites it. This flag is passed in
  // explicitly (NOT inferred from the lock toggle): universal event-style tables
  // like medicationstatementtable have per-field provenance without a toggle.
  // Tables without it use the record's single row-level `source`.
  const recordSource = (data?.source as string) || "ai_extracted";
  const humanEditedFields = (data?.human_edited_fields as string[]) || [];
  const getFieldSource = (fieldName: string): string => {
    if (!perFieldProvenance) return recordSource;
    if (!humanEditedFields.includes(fieldName)) return "ai_extracted";
    return recordSource === "human_created" ? "human_created" : "human_edited";
  };
  const renderSourceBadge = (src: string) => {
    const human = src === "human_edited" || src === "human_created";
    const Icon = human ? PersonIcon : SmartToyIcon;
    const tip =
      src === "human_created"
        ? t("editable.source_user", "Created by user")
        : src === "human_edited"
        ? t("editable.source_edited", "Edited by user (AI extracted, then modified)")
        : t("editable.source_ai", "AI extracted");
    return (
      <Tooltip title={tip} arrow>
        <Icon
          sx={{
            fontSize: 14,
            flexShrink: 0,
            color: human
              ? alpha(theme.palette.primary.main, 0.8)
              : alpha(theme.palette.primary.dark, 0.55),
          }}
        />
      </Tooltip>
    );
  };

  const renderFieldRow = (fieldName: string) => {
    const info = fields[fieldName];
    const raw = data[fieldName];
    const label = getDisplayName(fieldName, info);
    const help = info?.help_text?.trim();
    const empty = isEmptyValue(raw);
    const fieldLocked = lockedFields.includes(fieldName);

    const labelNode = help ? (
      <Tooltip title={help} placement="top" arrow>
        <Typography component="span" sx={fieldNameSx}>
          {label}
        </Typography>
      </Tooltip>
    ) : (
      <Typography component="span" sx={fieldNameSx}>
        {label}
      </Typography>
    );

    return (
      <Box key={`${rowId}-${fieldName}`} sx={fieldRowGridSx}>
        <Box sx={{ minWidth: 0, pt: 0.25, display: "flex", alignItems: "center", gap: 0.5 }}>
          {renderSourceBadge(getFieldSource(fieldName))}
          {labelNode}
          {help && (
            <Tooltip title={help} placement="top" arrow>
              <HelpOutlineIcon
                sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0, cursor: "help" }}
              />
            </Tooltip>
          )}
          {/* Read-only lock indicator for tables WITHOUT a per-field toggle
              (record-level lock). Gated on the toggle affordance, not on
              `perFieldProvenance`, so universal event-style tables that show
              per-field badges but no toggle still surface the record lock.
              Tables with the toggle show it (and its lock state) on the right. */}
          {!onToggleFieldLock && isLocked && (
            <Tooltip
              title={t("editable.lock_tooltip", "Locked — AI will not modify this record")}
              arrow
            >
              <LockIcon
                sx={{ fontSize: 13, color: theme.palette.warning.main, flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              ...(empty
                ? {
                    "& .MuiOutlinedInput-root": { opacity: 0.72 },
                    "& .MuiCheckbox-root": { opacity: 0.72 },
                    "& .MuiTypography-root": { opacity: 0.65 },
                  }
                : {}),
            }}
          >
            <EditableCell
              value={raw}
              fieldName={fieldName}
              fieldInfo={info}
              patientId={data.patient}
              onSave={onSave}
              disabled={isLocked}
            />
          </Box>
          {/* Per-field lock toggle (cursor_logs/039). Hidden when the whole
              record is locked (row-level lock already protects every field). */}
          {!isLocked && onToggleFieldLock && (
            <Tooltip
              arrow
              title={
                fieldLocked
                  ? t(
                      "editable.unlock_field",
                      "Unlock field (let AI update it again)",
                    )
                  : t(
                      "editable.lock_field",
                      "Lock field (protect from AI changes)",
                    )
              }
            >
              <IconButton
                size="small"
                onClick={() => onToggleFieldLock(fieldName, !fieldLocked)}
                sx={{ mt: 0.25 }}
              >
                {fieldLocked ? (
                  <LockIcon
                    sx={{ fontSize: 15, color: theme.palette.warning.main }}
                  />
                ) : (
                  <LockOpenIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const titleContent = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        position: "relative",
        zIndex: 3,
        pt: 2.5,
        pb: 1,
        px: 3,
        color: athenaColors.white,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
        <Box sx={athenaHeaderIconBoxSx}>
          <TableChartIcon sx={{ color: athenaColors.periwinkle, fontSize: 22 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} noWrap sx={{ color: athenaColors.white }}>
          {title}
        </Typography>
      </Box>
      <IconButton
        size="small"
        onClick={onClose}
        aria-label={t("fullFieldModal.close_aria", "Close dialog")}
        sx={{
          color: alpha(athenaColors.white, 0.65),
          "&:hover": { color: athenaColors.white, backgroundColor: alpha(athenaColors.white, 0.08) },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );

  // Imaging rows carry a resolved, read-only `image_url` (one row == one synced
  // disease image). Show it larger here alongside the metadata fields; clicking
  // opens the in-app lightbox. Display-only: `image_url` / `image_document` are
  // excluded from the editable field list.
  const imageUrl = (data?.image_url as string | undefined) || undefined;

  // Ordered lesion-image URLs for the Diagnosis gallery carousel.
  const galleryImageUrls = useMemo(
    () =>
      linkedImages
        .map((img) => img.image_url as string | undefined)
        .filter((u): u is string => !!u),
    [linkedImages]
  );

  const bodyContent = (
    <Box sx={{ position: "relative", zIndex: 3, px: 3, pt: 1, pb: 2 }}>
      {imageUrl && (
        <Tooltip title={t("imaging.view_image", "View full image")} arrow>
          <Box
            component="img"
            src={imageUrl}
            alt={t("imaging.image_alt", "Imaging study")}
            loading="lazy"
            onClick={() => setLightbox({ open: true, index: 0, images: [imageUrl] })}
            sx={{
              display: "block",
              maxWidth: "100%",
              maxHeight: 320,
              mb: 2,
              borderRadius: 2,
              objectFit: "contain",
              cursor: "pointer",
              border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`,
              transition: "border-color 0.15s ease",
              "&:hover": { borderColor: athenaColors.periwinkle },
            }}
          />
        </Tooltip>
      )}
      <TextField
        fullWidth
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("fullFieldModal.search_placeholder", "Search fields...")}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: alpha(athenaColors.white, 0.45), fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, mt: 0.5 }}
      />

      {filteredFieldKeys.length === 0 ? (
        <Typography variant="body2" sx={{ color: alpha(athenaColors.white, 0.55), py: 2 }}>
          {t("fullFieldModal.no_matching_fields", "No fields match your search.")}
        </Typography>
      ) : (
        <>
          {primarySections.map((section, idx) => (
            <Box
              key={section.title || `section-${idx}`}
              ref={section.title ? registerSectionRef(section.title) : undefined}
              data-section-title={section.title || undefined}
              sx={{ mb: idx < primarySections.length - 1 ? 1 : 0, scrollMarginTop: 8 }}
            >
              {section.title ? (
                <Typography component="h3" sx={categoryHeaderSx}>
                  {section.title}
                </Typography>
              ) : null}
              {section.keys.map(renderFieldRow)}
            </Box>
          ))}

          {otherSections.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                onClick={handleToggleOther}
                endIcon={<ExpandMoreIcon sx={{
                  transform: showOtherCategories ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }} />}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: alpha(athenaColors.periwinkle, 0.7),
                  "&:hover": {
                    color: athenaColors.periwinkle,
                    backgroundColor: alpha(athenaColors.periwinkle, 0.08),
                  },
                }}
              >
                {showOtherCategories
                  ? t("fullFieldModal.hide_other_categories", "Hide other categories")
                  : t("fullFieldModal.show_other_categories", "Show other categories")}
              </Button>
              <Collapse in={showOtherCategories} timeout={200}>
                {otherSections.map((section, idx) => (
                  <Box key={section.title || `other-${idx}`} sx={{ mb: idx < otherSections.length - 1 ? 1 : 0 }}>
                    {section.title ? (
                      <Typography component="h3" sx={categoryHeaderSx}>
                        {section.title}
                      </Typography>
                    ) : null}
                    {section.keys.map(renderFieldRow)}
                  </Box>
                ))}
              </Collapse>
            </Box>
          )}
        </>
      )}
    </Box>
  );

  const footerContent = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        position: "relative",
        zIndex: 3,
        px: 3,
        py: 2,
        borderTop: `1px solid ${alpha(athenaColors.white, 0.1)}`,
      }}
    >
      <Button variant="outlined" onClick={onClose} sx={athenaDarkSecondaryButtonSx}>
        {t("fullFieldModal.close", "Close")}
      </Button>
    </Box>
  );

  return (
    <>
    <ImageLightbox
      open={lightbox.open}
      images={lightbox.images}
      index={lightbox.index}
      onIndexChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
      onClose={() => setLightbox((s) => ({ ...s, open: false }))}
    />
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{ backdrop: { sx: athenaBackdropSx } }}
      PaperProps={{ sx: glassVideoPaperSx }}
    >
      <Box sx={{ position: "relative", overflow: "hidden", borderRadius: 3 }}>
        {/* Gradient background */}
        <Box sx={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${alpha(athenaColors.deepNavy, 0.95)} 0%, ${alpha(athenaColors.deepNavy, 0.85)} 50%, ${alpha(athenaColors.royalBlue, 0.78)} 100%)`, zIndex: 0 }} />
        {/* Background, hidden, retained for future use. It was a <video> at
            opacity 0: invisible, yet decoding 1280x720 every frame. */}
        <AppBackdrop sx={{ opacity: 0 }} />
        {/* Gradient overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(160deg, ${alpha(athenaColors.deepNavy, 0.95)} 0%, ${alpha(
              athenaColors.deepNavy,
              0.85
            )} 50%, ${alpha(athenaColors.royalBlue, 0.78)} 100%)`,
            zIndex: 1,
          }}
        />
        {/* Glass card */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            m: 1.5,
            borderRadius: 2.5,
            background: alpha(athenaColors.deepNavy, 0.55),
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: `1px solid ${alpha(athenaColors.white, 0.22)}`,
            maxHeight: "min(82vh, 850px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            color: athenaColors.white,
            "& .MuiTypography-root": { color: athenaColors.white },
            "& .MuiOutlinedInput-root": {
              backgroundColor: alpha(athenaColors.white, 0.07),
              color: athenaColors.white,
              "& fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
              "&:hover fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.55) },
              "&.Mui-focused fieldset": { borderColor: athenaColors.periwinkle },
            },
            "& .MuiOutlinedInput-input": {
              color: athenaColors.white,
              "&::placeholder": { color: alpha(athenaColors.white, 0.45), opacity: 1 },
            },
            "& .MuiSvgIcon-root": { color: alpha(athenaColors.white, 0.5) },
            "& .MuiSelect-icon": { color: alpha(athenaColors.white, 0.5) },
            "& .MuiCheckbox-root": {
              color: alpha(athenaColors.white, 0.5),
              "&.Mui-checked": { color: athenaColors.periwinkle },
            },
          }}
        >
          {titleContent}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
            {showNav && (
              <Box
                component="nav"
                aria-label={t("fullFieldModal.sections_nav_aria", "Field sections")}
                sx={{
                  width: 210,
                  flexShrink: 0,
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                  py: 2,
                  px: 1.25,
                  borderRight: `1px solid ${alpha(athenaColors.periwinkle, 0.18)}`,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-thumb": { backgroundColor: alpha(athenaColors.periwinkle, 0.3), borderRadius: 3 },
                  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                }}
              >
                <Typography sx={{ ...categoryHeaderSx, pt: 0, mb: 1, px: 1 }}>
                  {t("fullFieldModal.sections_nav_title", "Sections")}
                </Typography>
                {navSections.map((s) => {
                  const active = activeSection === s.title;
                  return (
                    <Box
                      key={s.title}
                      role="button"
                      tabIndex={0}
                      onClick={() => scrollToSection(s.title)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          scrollToSection(s.title);
                        }
                      }}
                      sx={{
                        cursor: "pointer",
                        py: 0.65,
                        px: 1,
                        mb: 0.25,
                        borderRadius: 1,
                        borderLeft: `2px solid ${active ? athenaColors.periwinkle : "transparent"}`,
                        backgroundColor: active ? alpha(athenaColors.periwinkle, 0.14) : "transparent",
                        transition: "background-color 0.15s ease, border-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: alpha(athenaColors.periwinkle, active ? 0.16 : 0.09),
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: active ? 700 : 500,
                          lineHeight: 1.3,
                          color: active ? athenaColors.white : alpha(athenaColors.white, 0.7),
                        }}
                      >
                        {s.title}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            <Box
              ref={scrollRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                // Stop the scroll from chaining to the page behind the modal
                // once the panel hits its top/bottom.
                overscrollBehavior: "contain",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": { backgroundColor: alpha(athenaColors.periwinkle, 0.3), borderRadius: 3 },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
              }}
            >
              {linkedImages.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      ...categoryHeaderSx,
                      pt: 0,
                    }}
                  >
                    {t("diagnosis.linked_images", "Lesion images")}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                    {linkedImages.map((img, idx) => (
                      <ImageThumbnail
                        key={img.event_id ?? img.id ?? idx}
                        imageUrl={img.image_url as string | undefined}
                        size={56}
                        onOpen={
                          img.image_url
                            ? () => {
                                const at = galleryImageUrls.indexOf(
                                  img.image_url as string
                                );
                                setLightbox({
                                  open: true,
                                  index: at >= 0 ? at : 0,
                                  images: galleryImageUrls,
                                });
                              }
                            : undefined
                        }
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {bodyContent}
            </Box>
          </Box>
          {footerContent}
        </Box>
      </Box>
    </Dialog>
    </>
  );
};

export default FullFieldModal;
